/**
 * Admin CRUD for Manager & Seller accounts.
 * Only Admin can create, rename, change role/password, deactivate or delete
 * the personal logins that managers and vendedores use.
 */
import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Users, UserPlus, Trash2, KeyRound, ToggleLeft, ToggleRight,
  Search, ShieldCheck, X, Save, Store as StoreIcon, Loader2,
  Download, AlertTriangle, CheckCircle2, Link2, Copy, Send, Crown,
  UploadCloud, UserCog, FileSpreadsheet, RotateCcw, LockOpen, Clock,
} from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";

const ROLE_LABELS = {
  manager: { es: "Manager", en: "Manager", color: "#005BBB" },
  seller:  { es: "Vendedor", en: "Seller", color: "#E3000F" },
};

const emptyForm = {
  username: "", name: "", password: "", role: "seller", store: "",
  email: "", entity: "", location: "", title: "", manager_id: "",
};

const originForInvite = () => {
  if (typeof window === "undefined") return "";
  return window.location.origin;
};

const copyToClipboard = async (text) => {
  if (navigator?.clipboard?.writeText) {
    try { await navigator.clipboard.writeText(text); return true; } catch { /* fallthrough */ }
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed"; ta.style.opacity = "0";
  document.body.appendChild(ta); ta.select();
  const ok = document.execCommand("copy");
  document.body.removeChild(ta);
  return ok;
};

export default function Staff() {
  const { lang } = useLang();
  const { user: me } = useAuth() || {};
  const readOnly = me?.role === "admin-lite";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null for create, obj for edit
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);
  const [entityFilter, setEntityFilter] = useState("all");
  const [groupBy, setGroupBy] = useState("none"); // 'none' | 'entity' | 'location'
  const [bulkBusy, setBulkBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignBusy, setAssignBusy] = useState(false);
  const [assignResult, setAssignResult] = useState(null);
  const [managers, setManagers] = useState([]);

  const loadManagers = async () => {
    try {
      const { data } = await api.get("/admin/staff/managers");
      setManagers(data || []);
    } catch (_) { /* soft failure */ }
  };
  useEffect(() => { loadManagers(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/staff");
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error cargando cuentas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const entities = useMemo(() => {
    const s = new Set();
    for (const u of items) {
      const e = (u.entity || "").trim();
      if (e) s.add(e);
    }
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [items]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (roleFilter !== "all" && i.role !== roleFilter) return false;
      if (entityFilter !== "all" && (i.entity || "") !== entityFilter) return false;
      if (!q) return true;
      return (
        (i.username || "").toLowerCase().includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.name || "").toLowerCase().includes(q) ||
        (i.store || "").toLowerCase().includes(q) ||
        (i.location || "").toLowerCase().includes(q) ||
        (i.entity || "").toLowerCase().includes(q) ||
        (i.title || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, roleFilter, entityFilter]);

  const counts = useMemo(() => {
    const c = { total: items.length, manager: 0, seller: 0, pendingFirstLogin: 0 };
    for (const u of items) {
      if (u.role === "manager") c.manager++;
      else if (u.role === "seller") c.seller++;
      if (u.must_change_password) c.pendingFirstLogin++;
    }
    return c;
  }, [items]);

  const runImport = async () => {
    if (!window.confirm(
      lang === "es"
        ? "Importar el roster desde Excel? Esto creará/actualizará cuentas y asignará la contraseña temporal Canales2026 a las nuevas."
        : "Import roster from Excel? This will create/update accounts using the temporary password Canales2026."
    )) return;
    setImportBusy(true);
    try {
      const { data } = await api.post("/admin/staff/import-roster", {
        temp_password: "Canales2026",
      });
      setImportResult(data);
      toast.success(
        lang === "es"
          ? `Importadas ${data.created} nuevas, ${data.updated} actualizadas.`
          : `Imported ${data.created} new, ${data.updated} updated.`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error importando");
    } finally {
      setImportBusy(false);
    }
  };

  // ==================== Invite links (single-use, 7 days) ====================
  const generateInvitesFor = async (users, rotate = false) => {
    if (!users?.length) return null;
    const { data } = await api.post("/admin/staff/invites", {
      user_ids: users.map((u) => u.user_id),
      rotate,
    });
    return data;
  };

  const formatInviteBlock = (invites) => {
    // Group per (entity, location) so managers can WhatsApp their team en bloque.
    const origin = originForInvite();
    const buckets = new Map();
    for (const inv of invites) {
      const key = `${inv.entity || ""} · ${inv.location || ""}`.trim();
      if (!buckets.has(key)) buckets.set(key, []);
      buckets.get(key).push(inv);
    }
    const lines = [];
    for (const [key, invs] of buckets.entries()) {
      lines.push(`=== ${key || "Canales"} ===`);
      for (const inv of invs) {
        const roleLabel = inv.role === "manager" ? "Manager" : "Vendedor";
        lines.push(`${inv.name} (${roleLabel}) — ${inv.email}`);
        // Backend HTML gateway `/api/i/{token}` renders per-user OG meta so
        // WhatsApp/iMessage show a personalised "¡Bienvenido, {Name}!" card.
        lines.push(`  ${origin}${inv.share_path || `/api/i/${inv.token}`}`);
      }
      lines.push("");
    }
    lines.push(
      lang === "es"
        ? `Cada link expira en 7 días y sólo se puede usar UNA vez.`
        : `Each link expires in 7 days and can be used only ONCE.`
    );
    return lines.join("\n");
  };

  const copyLinkForUser = async (u) => {
    try {
      const data = await generateInvitesFor([u]);
      const inv = data?.invites?.[0];
      if (!inv) throw new Error("no invite");
      const url = `${originForInvite()}${inv.share_path || `/api/i/${inv.token}`}`;
      const ok = await copyToClipboard(url);
      if (ok) {
        toast.success(
          lang === "es"
            ? `Link copiado para ${u.name}`
            : `Link copied for ${u.name}`
        );
      } else {
        toast.message(
          lang === "es"
            ? `Copia manual: ${url}`
            : `Manual copy: ${url}`,
          { duration: 15000 }
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const copyBulkLinks = async (users, label) => {
    if (!users?.length) {
      toast.error(lang === "es" ? "Nada que copiar." : "Nothing to copy.");
      return;
    }
    if (users.length > 30 && !window.confirm(
      lang === "es"
        ? `Se generarán ${users.length} links y se copiarán al portapapeles. ¿Continuar?`
        : `${users.length} links will be generated and copied. Continue?`
    )) return;
    setBulkBusy(true);
    try {
      const data = await generateInvitesFor(users);
      const text = formatInviteBlock(data?.invites || []);
      const ok = await copyToClipboard(text);
      if (ok) {
        toast.success(
          lang === "es"
            ? `${data.count} links copiados${label ? ` (${label})` : ""}. Pégalos en WhatsApp/Telegram.`
            : `${data.count} links copied${label ? ` (${label})` : ""}.`
        );
      } else {
        toast.message(
          lang === "es"
            ? "No se pudo copiar. Abre la consola para el bloque."
            : "Copy failed. Check the console for the block."
        );
        console.log(text);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error generando links");
    } finally {
      setBulkBusy(false);
    }
  };

  const promoteToAdmin = async (u) => {
    if (!u?.user_id) return;
    if (!window.confirm(
      lang === "es"
        ? `¿Convertir a ${u.name} (${u.email}) en Admin? Podrá entrar al Panel Admin con su email + contraseña actual.`
        : `Make ${u.name} (${u.email}) an Admin? They'll be able to sign into the Admin panel with their current email + password.`
    )) return;
    try {
      const { data } = await api.post(`/admin/staff/${u.user_id}/promote-to-admin`);
      toast.success(
        lang === "es"
          ? `✓ ${u.name} es Admin (rol=${data.new_role})`
          : `✓ ${u.name} is now Admin (role=${data.new_role})`
      );
      load();
      loadManagers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const demoteFromAdmin = async (u) => {
    if (!u?.user_id) return;
    if (!window.confirm(
      lang === "es"
        ? `¿Revocar los permisos de Admin a ${u.name}? Volverá a rol Manager y saldrá de su sesión.`
        : `Revoke Admin privileges from ${u.name}? They'll go back to Manager role and lose their session.`
    )) return;
    try {
      const { data } = await api.post(`/admin/staff/${u.user_id}/demote-from-admin`);
      toast.success(
        lang === "es"
          ? `Admin revocado. Ahora es ${data.new_role}.`
          : `Admin revoked. Now they're ${data.new_role}.`
      );
      load();
      loadManagers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const resetPassword = async (u) => {
    if (!u?.user_id) return;
    if (!window.confirm(
      lang === "es"
        ? `¿Resetear la contraseña de ${u.name} a "Canales2026"? Deberá cambiarla en el próximo login y sus sesiones activas se cerrarán.`
        : `Reset ${u.name}'s password to "Canales2026"? They'll have to change it on next login and any active sessions will be killed.`
    )) return;
    try {
      const { data } = await api.post(`/admin/staff/${u.user_id}/reset-password`);
      toast.success(
        lang === "es"
          ? `Contraseña reseteada a "${data.temp_password}". Sesiones cerradas: ${data.sessions_killed}`
          : `Password reset to "${data.temp_password}". Sessions killed: ${data.sessions_killed}`,
        { duration: 6000 }
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const unblockStaff = async (u) => {
    if (!u?.user_id) return;
    try {
      await api.post(`/admin/staff/${u.user_id}/unblock`);
      toast.success(lang === "es" ? "Cuenta reactivada" : "Account re-activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  // Build the group buckets when groupBy !== 'none'.
  const grouped = useMemo(() => {
    if (groupBy === "none") return null;
    const map = new Map();
    for (const u of filtered) {
      const key = ((groupBy === "entity" ? u.entity : (u.location || u.store)) || "").trim() || (lang === "es" ? "(Sin asignar)" : "(Unassigned)");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    }
    return Array.from(map.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([key, rows]) => ({ key, rows }));
  }, [filtered, groupBy, lang]);

  // Single row rendering — reused in both the flat and grouped views.
  const renderRow = (u) => (
    <tr
      key={u.user_id}
      data-testid={`staff-row-${u.username}`}
      className={`border-b border-neutral-200 ${u.active === false ? "opacity-50" : ""}`}
    >
      <td className="px-4 py-3 font-display font-bold">
        <div className="flex items-center gap-2">
          {u.name}
          {u.must_change_password && (
            <span
              title={lang === "es" ? "Aún no cambia su contraseña temporal" : "Hasn't rotated temp password yet"}
              className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-canales-yellow border border-black font-body font-bold"
            >
              <AlertTriangle className="w-3 h-3" />
              {lang === "es" ? "1er login" : "1st login"}
            </span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 font-mono text-neutral-700">{u.username}</td>
      <td className="px-4 py-3">
        <span
          className="inline-block px-2 py-1 text-[10px] font-display font-black uppercase tracking-widest text-white rounded-full"
          style={{ background: ROLE_LABELS[u.role]?.color || "#666" }}
        >
          {ROLE_LABELS[u.role]?.[lang] || u.role}
        </span>
      </td>
      <td className="px-4 py-3">
        {u.store || u.location ? (
          <span className="inline-flex items-center gap-1 text-neutral-700">
            <StoreIcon className="w-3 h-3" />
            {u.store || u.location}
          </span>
        ) : (
          <span className="text-neutral-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-neutral-600 text-xs">
        {u.role === "seller" ? (
          u.manager_name ? (
            <span className="inline-flex items-center gap-1">
              <UserCog className="w-3 h-3 text-canales-blue" />
              {u.manager_name}
            </span>
          ) : (
            <span className="text-canales-red font-display font-bold text-[10px] uppercase tracking-widest">
              {lang === "es" ? "Sin asignar" : "Unassigned"}
            </span>
          )
        ) : (
          <span className="text-neutral-400">—</span>
        )}
      </td>
      <td className="px-4 py-3 text-neutral-600 text-xs hidden lg:table-cell">
        {u.title || <span className="text-neutral-400">—</span>}
      </td>
      <td className="px-4 py-3">
        <button
          onClick={() => toggleActive(u)}
          data-testid={`staff-toggle-${u.username}`}
          className="inline-flex items-center gap-1 text-xs font-display font-bold"
          title={lang === "es" ? "Activar / desactivar" : "Enable / disable"}
        >
          {u.active === false ? (
            <>
              <ToggleLeft className="w-5 h-5 text-canales-red" />
              <span className="text-canales-red">{lang === "es" ? "Bloqueado" : "Blocked"}</span>
            </>
          ) : u.must_change_password ? (
            <>
              <Clock className="w-5 h-5 text-orange-600" />
              <span className="text-orange-700">{lang === "es" ? "Pendiente 1er login" : "1st login pending"}</span>
            </>
          ) : (
            <>
              <ToggleRight className="w-5 h-5 text-green-700" />
              <span className="text-green-700">{lang === "es" ? "Activo" : "Active"}</span>
            </>
          )}
        </button>
      </td>
      <td className="px-4 py-3 text-right">
        <div className="inline-flex gap-1 flex-wrap justify-end">
          <button
            onClick={() => copyLinkForUser(u)}
            disabled={readOnly}
            data-testid={`staff-copy-link-${u.username}`}
            className="brutal-sm px-2 py-1 bg-canales-blue text-white text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            title={readOnly ? (lang === "es" ? "Solo lectura" : "Read-only") : (lang === "es" ? "Copiar link de invitación (7 días, un solo uso)" : "Copy invite link (7 days, single use)")}
          >
            <Link2 className="w-3 h-3" />
            Link
          </button>
          {u.role === "manager" && u.email && !u.email.endsWith("@canales.local") && (
            <button
              onClick={() => promoteToAdmin(u)}
              disabled={readOnly}
              data-testid={`staff-promote-${u.username}`}
              className="brutal-sm px-2 py-1 bg-canales-yellow text-black text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              title={lang === "es" ? "Convertir en Admin (login con email+password)" : "Promote to Admin (email+password login)"}
            >
              <Crown className="w-3 h-3" />
              {lang === "es" ? "Hacer Admin" : "Make Admin"}
            </button>
          )}
          {u.role === "admin" && (
            <button
              onClick={() => demoteFromAdmin(u)}
              disabled={readOnly}
              data-testid={`staff-demote-${u.username}`}
              className="brutal-sm px-2 py-1 bg-canales-red text-white text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              title={lang === "es" ? "Revocar admin (vuelve a Manager)" : "Demote to Manager"}
            >
              <Crown className="w-3 h-3" />
              {lang === "es" ? "Quitar Admin" : "Remove Admin"}
            </button>
          )}
          <button
            onClick={() => resetPassword(u)}
            disabled={readOnly}
            data-testid={`staff-reset-pw-${u.username}`}
            className="brutal-sm px-2 py-1 bg-canales-cream text-black text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            title={lang === "es" ? "Resetear contraseña a Canales2026 y forzar cambio" : "Reset password to Canales2026 and force change"}
          >
            <RotateCcw className="w-3 h-3" />
            {lang === "es" ? "Reset PW" : "Reset PW"}
          </button>
          {u.active === false && (
            <button
              onClick={() => unblockStaff(u)}
              disabled={readOnly}
              data-testid={`staff-unblock-${u.username}`}
              className="brutal-sm px-2 py-1 bg-green-600 text-white text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
              title={lang === "es" ? "Reactivar cuenta bloqueada" : "Re-activate blocked account"}
            >
              <LockOpen className="w-3 h-3" />
              {lang === "es" ? "Desbloquear" : "Unblock"}
            </button>
          )}
          <button
            onClick={() => openEdit(u)}
            disabled={readOnly}
            data-testid={`staff-edit-${u.username}`}
            className="brutal-sm px-2 py-1 bg-canales-yellow text-black text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <KeyRound className="w-3 h-3" />
            {lang === "es" ? "Editar" : "Edit"}
          </button>
          <button
            onClick={() => remove(u)}
            disabled={readOnly}
            data-testid={`staff-delete-${u.username}`}
            className="brutal-sm px-2 py-1 bg-white text-canales-red text-xs font-display font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 className="w-3 h-3" />
            {lang === "es" ? "Borrar" : "Delete"}
          </button>
        </div>
      </td>
    </tr>
  );

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({
      username: u.username,
      name: u.name,
      password: "",
      role: u.role,
      store: u.store || "",
      email: u.email || "",
      entity: u.entity || "",
      location: u.location || "",
      title: u.title || "",
      manager_id: u.manager_id || "",
    });
    setModalOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error(lang === "es" ? "El nombre es obligatorio" : "Name required");
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const body = {
          name: form.name,
          role: form.role,
          store: form.store,
          email: form.email,
          entity: form.entity,
          location: form.location,
          title: form.title,
          manager_id: form.manager_id,
        };
        if (form.password) body.password = form.password;
        await api.put(`/admin/staff/${editing.user_id}`, body);
        toast.success(lang === "es" ? "Guardado" : "Saved");
      } else {
        if (!form.username.trim() || !form.password) {
          toast.error(lang === "es" ? "Usuario y contraseña requeridos" : "Username and password required");
          setBusy(false);
          return;
        }
        await api.post("/admin/staff", form);
        toast.success(lang === "es" ? "Cuenta creada" : "Account created");
      }
      setModalOpen(false);
      load();
      loadManagers();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/admin/staff/${u.user_id}`, { active: !u.active });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const remove = async (u) => {
    const label = `${u.name} (${u.username})`;
    if (!window.confirm(lang === "es" ? `¿Borrar la cuenta ${label}?` : `Delete ${label}?`)) return;
    try {
      await api.delete(`/admin/staff/${u.user_id}`);
      toast.success(lang === "es" ? "Cuenta borrada" : "Account deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
      data-testid="admin-staff-page"
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display font-black text-4xl flex items-center gap-3">
            <Users className="w-8 h-8 text-canales-blue" />
            {lang === "es" ? "Cuentas Manager & Vendedor" : "Manager & Seller Accounts"}
          </h1>
          <p className="font-body text-neutral-700 mt-1">
            {lang === "es"
              ? "Crea logins personales. Solo tú (admin) puedes editar o borrar cuentas."
              : "Create personal logins. Only you (admin) can edit or delete accounts."}
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <Search className="absolute left-2 top-2.5 w-4 h-4 text-neutral-500" />
            <Input
              data-testid="staff-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === "es" ? "Buscar nombre, tienda, título…" : "Search name, store, title…"}
              className="brutal-sm pl-8 py-2 w-64"
            />
          </div>
          <select
            data-testid="staff-role-filter"
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="brutal-sm px-3 py-2 bg-white font-body text-sm"
          >
            <option value="all">{lang === "es" ? "Todos los roles" : "All roles"}</option>
            <option value="manager">{lang === "es" ? "Solo Managers" : "Managers only"}</option>
            <option value="seller">{lang === "es" ? "Solo Vendedores" : "Sellers only"}</option>
          </select>
          <select
            data-testid="staff-entity-filter"
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className="brutal-sm px-3 py-2 bg-white font-body text-sm max-w-[220px]"
            title={lang === "es" ? "Filtrar por entidad / franquicia" : "Filter by entity / franchise"}
          >
            <option value="all">{lang === "es" ? "Todas las entidades" : "All entities"}</option>
            {entities.map((e) => (
              <option key={e} value={e}>{e}</option>
            ))}
          </select>
          <select
            data-testid="staff-group-by"
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value)}
            className="brutal-sm px-3 py-2 bg-white font-body text-sm"
            title={lang === "es" ? "Agrupar por tienda o entidad" : "Group by store or entity"}
          >
            <option value="none">{lang === "es" ? "Sin agrupar" : "No grouping"}</option>
            <option value="location">{lang === "es" ? "Por tienda / ubicación" : "By store / location"}</option>
            <option value="entity">{lang === "es" ? "Por entidad / franquicia" : "By entity / franchise"}</option>
          </select>
          <button
            data-testid="staff-copy-all-btn"
            onClick={() => copyBulkLinks(filtered, lang === "es" ? "filtrados" : "filtered")}
            disabled={bulkBusy || !filtered.length}
            className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2 font-display font-black disabled:opacity-60"
            title={lang === "es" ? "Genera y copia todos los links visibles" : "Generate & copy every visible link"}
          >
            {bulkBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            {lang === "es" ? `Copiar ${filtered.length} link${filtered.length === 1 ? "" : "s"}` : `Copy ${filtered.length} link${filtered.length === 1 ? "" : "s"}`}
          </button>
          <button
            data-testid="staff-assign-managers-btn"
            onClick={() => { setAssignResult(null); setAssignOpen(true); }}
            disabled={readOnly}
            className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2 font-display font-black disabled:opacity-60"
            title={lang === "es" ? "Subir Excel con seller_email → manager_email" : "Upload Excel with seller_email → manager_email"}
          >
            <UserCog className="w-4 h-4" />
            {lang === "es" ? "Asignar managers" : "Assign managers"}
          </button>
          <button
            data-testid="staff-import-btn"
            onClick={runImport}
            disabled={importBusy}
            className="brutal-btn bg-canales-yellow text-black px-4 py-2 flex items-center gap-2 font-display font-black disabled:opacity-60"
          >
            {importBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {lang === "es" ? "Importar roster" : "Import roster"}
          </button>
          <button
            data-testid="staff-create-btn"
            onClick={openCreate}
            className="brutal-btn bg-canales-red text-white px-4 py-2 flex items-center gap-2 font-display font-black"
          >
            <UserPlus className="w-4 h-4" />
            {lang === "es" ? "Nueva cuenta" : "New account"}
          </button>
        </div>
      </div>

      {/* Roster summary badges */}
      <div className="flex flex-wrap gap-2 text-xs font-display font-black uppercase tracking-widest">
        <span className="brutal-sm px-3 py-1 bg-white">
          {lang === "es" ? "Total" : "Total"}: {counts.total}
        </span>
        <span className="brutal-sm px-3 py-1 bg-canales-blue text-white">
          Managers: {counts.manager}
        </span>
        <span className="brutal-sm px-3 py-1 bg-canales-red text-white">
          {lang === "es" ? "Vendedores" : "Sellers"}: {counts.seller}
        </span>
        {counts.pendingFirstLogin > 0 && (
          <span className="brutal-sm px-3 py-1 bg-canales-yellow flex items-center gap-1">
            <AlertTriangle className="w-3 h-3" />
            {lang === "es" ? "Pendientes de 1er login" : "Pending first login"}: {counts.pendingFirstLogin}
          </span>
        )}
      </div>

      {importResult && (
        <div
          data-testid="staff-import-result"
          className="brutal p-4 bg-canales-cream flex items-start gap-3"
        >
          <CheckCircle2 className="w-6 h-6 text-green-700 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-black">
              {lang === "es" ? "Roster importado" : "Roster imported"}
            </div>
            <div className="text-sm font-body mt-1">
              {lang === "es"
                ? `${importResult.created} cuentas creadas · ${importResult.updated} actualizadas · contraseña temporal: `
                : `${importResult.created} accounts created · ${importResult.updated} updated · temporary password: `}
              <code className="brutal-sm bg-white px-2 py-0.5 font-mono">
                {importResult.temp_password}
              </code>
              . {lang === "es"
                ? "Cada usuario debe cambiarla en su primer login."
                : "Every user must change it on first login."}
            </div>
          </div>
          <button
            onClick={() => setImportResult(null)}
            className="p-1 text-neutral-500 hover:text-black"
            aria-label="close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="brutal bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin inline-block" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-neutral-500 font-body">
            {lang === "es"
              ? "Aún no hay cuentas. Crea la primera con el botón arriba a la derecha."
              : "No accounts yet. Create the first one with the button top-right."}
          </div>
        ) : grouped ? (
          <div className="divide-y-2 divide-neutral-200" data-testid="staff-grouped">
            {grouped.map(({ key, rows }) => (
              <details
                key={key}
                open
                className="group/table"
                data-testid={`staff-group-${key}`}
              >
                <summary className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-canales-cream cursor-pointer list-none [&::-webkit-details-marker]:hidden">
                  <div className="flex items-center gap-3">
                    <StoreIcon className="w-4 h-4 text-canales-blue" />
                    <span className="font-display font-black text-lg">{key}</span>
                    <span className="text-xs font-body text-neutral-600">
                      {rows.length} {lang === "es" ? "personas" : "people"}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      copyBulkLinks(rows, key);
                    }}
                    disabled={bulkBusy}
                    data-testid={`staff-group-copy-${key}`}
                    className="brutal-sm px-3 py-1 bg-canales-blue text-white text-xs font-display font-black inline-flex items-center gap-1 disabled:opacity-60"
                  >
                    {bulkBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />}
                    {lang === "es" ? `Copiar ${rows.length} link${rows.length === 1 ? "" : "s"}` : `Copy ${rows.length} link${rows.length === 1 ? "" : "s"}`}
                  </button>
                </summary>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <tbody>
                      {rows.map((u) => renderRow(u))}
                    </tbody>
                  </table>
                </div>
              </details>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-canales-cream border-b-2 border-black text-xs uppercase font-display font-black tracking-widest">
                <tr>
                  <th className="text-left px-4 py-3">{lang === "es" ? "Nombre" : "Name"}</th>
                  <th className="text-left px-4 py-3">{lang === "es" ? "Usuario" : "Username"}</th>
                  <th className="text-left px-4 py-3">{lang === "es" ? "Rol" : "Role"}</th>
                  <th className="text-left px-4 py-3">{lang === "es" ? "Tienda / Ubicación" : "Store / Location"}</th>
                  <th className="text-left px-4 py-3">{lang === "es" ? "Manager" : "Manager"}</th>
                  <th className="text-left px-4 py-3 hidden lg:table-cell">{lang === "es" ? "Título" : "Title"}</th>
                  <th className="text-left px-4 py-3">{lang === "es" ? "Estado" : "Status"}</th>
                  <th className="text-right px-4 py-3">{lang === "es" ? "Acciones" : "Actions"}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => renderRow(u))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ===== Modal ===== */}
      {modalOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          onClick={() => !busy && setModalOpen(false)}
          data-testid="staff-modal"
        >
          <div
            className="brutal p-6 bg-white w-full max-w-md space-y-4 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display font-black text-2xl flex items-center gap-2">
                <ShieldCheck className="w-6 h-6 text-canales-blue" />
                {editing
                  ? lang === "es" ? "Editar cuenta" : "Edit account"
                  : lang === "es" ? "Nueva cuenta" : "New account"}
              </h2>
              <button onClick={() => setModalOpen(false)} disabled={busy}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <label className="block">
              <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                {lang === "es" ? "Usuario" : "Username"}
              </span>
              <Input
                data-testid="staff-form-username"
                value={form.username}
                disabled={!!editing}
                onChange={(e) => setForm((f) => ({ ...f, username: e.target.value }))}
                placeholder="ana.perez"
                className="brutal-sm mt-1"
              />
              {editing && (
                <span className="text-xs text-neutral-500 mt-1 block">
                  {lang === "es"
                    ? "El usuario no se puede cambiar (crea otra cuenta si necesitas)."
                    : "Username can't change (create a new account instead)."}
                </span>
              )}
            </label>

            <label className="block">
              <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                {lang === "es" ? "Nombre completo" : "Full name"}
              </span>
              <Input
                data-testid="staff-form-name"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder={lang === "es" ? "Ana Pérez" : "Ana Perez"}
                className="brutal-sm mt-1"
              />
            </label>

            <label className="block">
              <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                {editing
                  ? lang === "es" ? "Nueva contraseña (opcional)" : "New password (optional)"
                  : lang === "es" ? "Contraseña" : "Password"}
              </span>
              <Input
                data-testid="staff-form-password"
                type="text"
                value={form.password}
                onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
                placeholder={editing ? "" : "min 4 chars"}
                className="brutal-sm mt-1"
              />
            </label>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Rol" : "Role"}
                </span>
                <select
                  data-testid="staff-form-role"
                  value={form.role}
                  onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}
                  className="brutal-sm mt-1 w-full px-3 py-2 bg-white font-body"
                >
                  <option value="seller">{lang === "es" ? "Vendedor" : "Seller"}</option>
                  <option value="manager">{lang === "es" ? "Manager" : "Manager"}</option>
                </select>
              </label>

              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Email corporativo" : "Corporate email"}
                </span>
                <Input
                  data-testid="staff-form-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="alguien@canalesfurniture.org"
                  className="brutal-sm mt-1"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Entidad / Franquicia" : "Entity / Franchise"}
                </span>
                <Input
                  data-testid="staff-form-entity"
                  value={form.entity}
                  onChange={(e) => setForm((f) => ({ ...f, entity: e.target.value }))}
                  placeholder="CE LOPEZ"
                  className="brutal-sm mt-1"
                />
              </label>

              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Ubicación / Tienda" : "Location / Store"}
                </span>
                <Input
                  data-testid="staff-form-location"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value, store: e.target.value }))}
                  placeholder="Duncanville"
                  className="brutal-sm mt-1"
                />
              </label>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Título / Puesto" : "Title / Position"}
                </span>
                <Input
                  data-testid="staff-form-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder={lang === "es" ? "Sales Rep, Store Manager…" : "Sales Rep, Store Manager…"}
                  className="brutal-sm mt-1"
                />
              </label>

              <label className="block">
                <span className="text-xs font-display font-black uppercase tracking-widest text-canales-red">
                  {lang === "es" ? "Manager asignado" : "Assigned manager"}
                </span>
                <select
                  data-testid="staff-form-manager"
                  value={form.manager_id}
                  onChange={(e) => setForm((f) => ({ ...f, manager_id: e.target.value }))}
                  className="brutal-sm mt-1 w-full px-3 py-2 bg-white font-body"
                  disabled={form.role !== "seller"}
                >
                  <option value="">{lang === "es" ? "— Sin asignar —" : "— Unassigned —"}</option>
                  {managers.map((m) => (
                    <option key={m.user_id} value={m.user_id}>
                      {m.name} {m.location ? `· ${m.location}` : ""}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setModalOpen(false)}
                disabled={busy}
                className="brutal-btn bg-white px-4 py-2"
              >
                {lang === "es" ? "Cancelar" : "Cancel"}
              </button>
              <button
                onClick={save}
                disabled={busy}
                data-testid="staff-form-save"
                className="brutal-btn bg-canales-blue text-white px-4 py-2 flex items-center gap-2 disabled:opacity-60"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {lang === "es" ? "Guardar" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Assign Managers upload modal ===== */}
      {assignOpen && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
          onClick={() => !assignBusy && setAssignOpen(false)}
          data-testid="assign-managers-modal"
        >
          <div
            className="brutal p-6 bg-white w-full max-w-lg space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-display font-black text-2xl flex items-center gap-2">
                <UserCog className="w-6 h-6 text-canales-blue" />
                {lang === "es" ? "Asignar managers en masa" : "Bulk assign managers"}
              </h2>
              <button onClick={() => setAssignOpen(false)} disabled={assignBusy}>
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="brutal-sm bg-canales-cream p-3 text-sm font-body space-y-2">
              <div className="flex items-start gap-2">
                <FileSpreadsheet className="w-4 h-4 mt-0.5 text-canales-blue shrink-0" />
                <div>
                  {lang === "es" ? (
                    <>
                      Sube un <b>Excel (.xlsx)</b> con dos columnas:
                      <code className="brutal-sm bg-white px-1 mx-1 font-mono">seller_email</code>
                      y
                      <code className="brutal-sm bg-white px-1 mx-1 font-mono">manager_email</code>.
                      Cada vendedor quedará vinculado a su manager. Emails ya
                      existentes se emparejan; los que no existan se reportan.
                    </>
                  ) : (
                    <>
                      Upload an <b>Excel (.xlsx)</b> with two columns:
                      <code className="brutal-sm bg-white px-1 mx-1 font-mono">seller_email</code>
                      and
                      <code className="brutal-sm bg-white px-1 mx-1 font-mono">manager_email</code>.
                      Each seller gets linked to their manager. Unknown emails
                      are reported back.
                    </>
                  )}
                </div>
              </div>
            </div>

            <label
              className="brutal border-dashed p-6 flex flex-col items-center gap-3 text-center cursor-pointer hover:bg-canales-cream/40"
              data-testid="assign-managers-dropzone"
            >
              <UploadCloud className="w-10 h-10 text-canales-blue" />
              <div className="font-display font-black text-lg">
                {assignBusy
                  ? (lang === "es" ? "Procesando…" : "Processing…")
                  : (lang === "es" ? "Elegir archivo .xlsx" : "Choose .xlsx file")}
              </div>
              <div className="text-xs font-body text-neutral-500">
                {lang === "es"
                  ? "El archivo se sube y procesa al instante — sin subir a servidores externos."
                  : "The file is processed instantly — no external upload."}
              </div>
              <input
                type="file"
                accept=".xlsx"
                disabled={assignBusy}
                className="hidden"
                data-testid="assign-managers-file"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("file", file);
                  setAssignBusy(true);
                  setAssignResult(null);
                  try {
                    const { data } = await api.post(
                      "/admin/staff/assign-managers/upload",
                      fd,
                      { headers: { "Content-Type": "multipart/form-data" } }
                    );
                    setAssignResult(data);
                    toast.success(
                      lang === "es"
                        ? `Asignados ${data.assigned} · sin resolver ${data.unresolved}`
                        : `Assigned ${data.assigned} · unresolved ${data.unresolved}`
                    );
                    load();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Error");
                  } finally {
                    setAssignBusy(false);
                    e.target.value = "";
                  }
                }}
              />
            </label>

            {assignResult && (
              <div
                className="brutal-sm bg-white p-3 text-sm font-body space-y-2 max-h-72 overflow-y-auto"
                data-testid="assign-managers-result"
              >
                <div className="flex flex-wrap gap-2">
                  <span className="brutal-sm px-2 py-0.5 bg-green-600 text-white text-xs font-display font-black">
                    ✓ {assignResult.assigned}
                  </span>
                  <span className="brutal-sm px-2 py-0.5 bg-canales-yellow text-xs font-display font-black">
                    ? {assignResult.unresolved}
                  </span>
                  <span className="brutal-sm px-2 py-0.5 bg-neutral-200 text-xs font-display font-black">
                    ⇄ {assignResult.duplicates}
                  </span>
                  {assignResult.total_rows !== undefined && (
                    <span className="brutal-sm px-2 py-0.5 bg-canales-cream text-xs font-display font-black">
                      {lang === "es" ? "Filas" : "Rows"}: {assignResult.total_rows}
                    </span>
                  )}
                </div>
                {assignResult.details?.unresolved?.length > 0 && (
                  <details>
                    <summary className="cursor-pointer font-display font-bold text-canales-red">
                      {lang === "es"
                        ? `Sin resolver (${assignResult.details.unresolved.length})`
                        : `Unresolved (${assignResult.details.unresolved.length})`}
                    </summary>
                    <ul className="mt-1 font-mono text-xs space-y-0.5">
                      {assignResult.details.unresolved.slice(0, 30).map((r, i) => (
                        <li key={i} className="text-neutral-700">
                          {r.seller_email || "?"} → {r.manager_email || "?"} · <span className="text-canales-red">{r.reason}</span>
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
                {assignResult.details?.assigned?.length > 0 && (
                  <details>
                    <summary className="cursor-pointer font-display font-bold text-green-700">
                      {lang === "es"
                        ? `Asignados (${assignResult.details.assigned.length})`
                        : `Assigned (${assignResult.details.assigned.length})`}
                    </summary>
                    <ul className="mt-1 font-mono text-xs space-y-0.5">
                      {assignResult.details.assigned.slice(0, 30).map((r, i) => (
                        <li key={i} className="text-neutral-700">
                          {r.seller_name} <span className="text-neutral-500">→</span> {r.manager_name}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setAssignOpen(false)}
                disabled={assignBusy}
                className="brutal-btn bg-white px-4 py-2"
              >
                {lang === "es" ? "Cerrar" : "Close"}
              </button>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
}
